import React from "react";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import clsx from "clsx";
import type { DashboardTrainingSession } from "../../../../../hooks/use-training-sessions.ts";
import { SessionEventTypeIcon } from "../session-event-type-icon.tsx";
import { formatSessionBannerPrimaryLine } from "../../utils/training-session-banner.ts";
import styles from "./celebration-banners.module.css";

export type SessionBannerCardProps = {
  session: DashboardTrainingSession;
  headline: string;
  /** @default "/dashboard/view-announcement" */
  to?: string;
};

function formatSessionDate(value: string): string | null {
  const calendarDate = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
  if (!calendarDate) return null;

  const [, year, month, day] = calendarDate;
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
  if (
    Number.isNaN(date.getTime()) ||
    date.getUTCMonth() + 1 !== Number(month) ||
    date.getUTCDate() !== Number(day)
  ) return null;

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function SessionBannerCard({
  session,
  headline,
  to = "/dashboard/view-announcement",
}: SessionBannerCardProps) {
  const primaryLine = formatSessionBannerPrimaryLine(
    headline,
    session.eventType,
  );
  const startDate = formatSessionDate(session.startDateTime);

  return (
    <Link
      to={to}
      className={styles.sessionCardLink}
      aria-label={`${primaryLine}. ${session.title}`}
    >
      <article className={clsx(styles.card, styles.cardSession)}>
        <div className={styles.leftContent}>
          <div className={styles.iconWrap}>
            <SessionEventTypeIcon
              eventType={session.eventType}
              className={styles.icon}
            />
          </div>
          <div className={styles.textBlock}>
            <p className={clsx("heading-xs mb-0", styles.headlineSession)}>
              {primaryLine}
            </p>
            <p className={clsx("sub-heading-xs mb-0", styles.sublineSession)}>
              {session.title}
            </p>
            {startDate && (
              <p className={clsx(styles.date, styles.sublineSession)}>
                <CalendarDays size={12} aria-hidden />
                <span>{startDate}</span>
              </p>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
}
